import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './select.module.scss';
import SimpleBar from 'simplebar-react';
import Input from '../input/input';
import { Icon } from '../icon';

type Props = {
  className?: string;
  field?: any;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'rounded-sm' | 'rounded-md' | 'rounded-lg' | 'rounded-xl';
  mode?: 'outline' | 'solid';
  color?: 'primary' | 'black' | 'white' | 'light';
  options: TOptionType[];
  selected?: string | number | boolean;
  onSelect: (option) => void;
  placeholder?: string;
  showReset?: boolean;
  showFilterOptions?: boolean;
};

export type TOptionType = {
  label: string;
  value: string | number | boolean;
};

export const Select = (props: Props) => {
  const {
    className,
    field,
    size,
    rounded,
    mode,
    color,
    options,
    selected,
    onSelect,
    placeholder,
    showReset = true,
    showFilterOptions = false,
  } = props;

  const [selectState, setSelectState] = useState({
    isSelectOpen: false,
    currentValue: selected,
  });

  const [optionsState, setOptionsState] = useState(props.options);

  const select = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  const handleOptionClick = (option: TOptionType) => {
    setSelectState({ isSelectOpen: false, currentValue: option.value });
    onSelect(option);
  };

  const clearValue = () => {
    setSelectState({ ...selectState, currentValue: '' });
    onSelect('');
  };

  const toggleSelect = () => {
    setSelectState({ ...selectState, isSelectOpen: !selectState.isSelectOpen });
  };

  const onOptionsFilter = event => {
    const filtered =
      event.target.value.length === 0
        ? props.options
        : props.options.filter(option => option.label.toLowerCase().includes(event.target.value.toLowerCase()));
    setOptionsState(filtered);
  };

  function handleClickOutside(event) {
    if (select && !select.current?.contains(event.target)) {
      setSelectState({ ...selectState, isSelectOpen: false });
      setOptionsState(props.options);
    }
  }

  useEffect(() => {
    setOptionsState(options);
  }, [options]);

  useEffect(() => {
    setSelectState({ ...selectState, currentValue: props.selected });
  }, [props.selected]);

  useEffect(() => {
    selectState.isSelectOpen && searchInput.current?.focus();
  }, [selectState.isSelectOpen]);

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const selectOptionClassName = value => {
    return cx(styles.SelectOption, {
      [styles.active]: selectState.currentValue === value,
    });
  };

  const OptionContainer = () => {
    return (
      <div className={styles.SelectOptions}>
        {showFilterOptions && (
          <div className={styles.SelectOptionsFilter}>
            <input ref={searchInput} type="text" placeholder="Cerca" onChange={onOptionsFilter} />
          </div>
        )}

        <SimpleBar>
          {optionsState.map((option, i) => (
            <div className={selectOptionClassName(option.value)} onClick={() => handleOptionClick(option)} key={i}>
              {option.label}
            </div>
          ))}
        </SimpleBar>
      </div>
    );
  };

  const getLabelByValue = value => {
    const selectedItem = options.filter(option => option.value === value);

    if (selectedItem.length > 0) {
      return selectedItem[0].label;
    }

    return '';
  };

  if (options.length === 0) {
    return <></>;
  }

  const selectClassName = cx(styles.Select, className);

  return (
    <div className={selectClassName} ref={select}>
      <>
        <Input
          className="cursor-pointer"
          placeholder={placeholder}
          rounded={rounded}
          size={size}
          mode={mode}
          color={color}
          field={{
            ...field,
            value: getLabelByValue(selectState.currentValue) || '',
            onClick: toggleSelect,
            readOnly: true,
          }}
        />
        {showReset && !!selectState.currentValue && (
          <div className={styles.SelectClear} onClick={clearValue}>
            <Icon content="close" />
          </div>
        )}
        {(!showReset || !selectState.currentValue) && (
          <div className={styles.SelectArrow} onClick={clearValue}>
            <Icon content="arrowDown" size="xs" color="black" />
          </div>
        )}
      </>

      {selectState.isSelectOpen && OptionContainer()}
    </div>
  );
};
